(() => {
  customElements.whenDefined('product-bundle').then(() => {
    if (customElements.get('inspirely-kit-bundle')) return;

    const NativeProductBundle = customElements.get('product-bundle');

    class InspirelyKitBundle extends NativeProductBundle {
      connectedCallback() {
        super.connectedCallback();
        this.stockConnection = {};
        this.stockCartRequest = null;
        this.stockRequestController = null;
        this.stockRowIds = new WeakMap();
        this.stockRowSequence = 0;
        this.stockCartVersion = 0;
        this.stockPreflight = false;
        this.stockRefreshing = false;
        this.stockCartFailure = false;
        this.stockReady = false;

        try {
          const inventory = JSON.parse(this.querySelector('script[data-inspirely-kit-inventory]').textContent);
          this.stockInventory = new Map(inventory.map((variant) => [String(variant.id), variant]));
          this.setStockCart(JSON.parse(this.querySelector('script[data-inspirely-kit-cart]').textContent));
          this.stockReady = true;
        } catch {
          this.stockCartFailure = true;
          this.stockInventory = new Map();
          this.stockCart = new Map();
        }

        this.on(this, 'input', this.onStockInput.bind(this));
        this.on(this, 'change', this.onStockInput.bind(this));
        this.registerCleanup(theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onStockCartUpdate.bind(this)));

        const observer = new MutationObserver(() => this.syncStockState());
        observer.observe(this.submitButton, { attributes: true, attributeFilter: ['aria-busy', 'aria-disabled'] });
        this.registerCleanup(() => observer.disconnect());
        this.registerCleanup(() => this.stockRequestController?.abort());
        this.syncStockState();
      }

      get kitLimit() {
        const value = Number(this.getAttribute('data-kit-limit'));
        return Number.isInteger(value) && value > 0 ? value : 96;
      }

      get kitRowLimit() {
        const value = Number(this.getAttribute('data-kit-row-limit'));
        return Number.isInteger(value) && value > 0 ? value : 96;
      }

      stockText(attribute, value) {
        return (this.getAttribute(attribute) || '').replace(/\[quantity\]|\[maximum\]/g, String(value));
      }

      setStockCart(items) {
        if (!Array.isArray(items)) throw new Error('Invalid cart quantities');
        const quantities = new Map();
        for (const item of items) {
          const id = String(item.variant_id ?? item.id);
          const quantity = Number(item.quantity);
          if (!Number.isInteger(quantity) || quantity < 0) throw new Error('Invalid cart quantity');
          quantities.set(id, (quantities.get(id) || 0) + quantity);
        }
        this.stockCart = quantities;
        this.stockCartVersion += 1;
        this.stockCartFailure = false;
      }

      getStockSelections() {
        return this.variants.filter((row) => !row.hasAttribute('available') && row.hasAttribute('data-variant-id')).map((row) => {
          const input = row.querySelector('quantity-input input');
          const quantity = input.value.trim() === '' ? NaN : Number(input.value);
          return { row, input, id: row.getAttribute('data-variant-id'), quantity };
        });
      }

      calculateStockState(selections) {
        const selectedQuantities = new Map();
        let total = 0;
        for (const selection of selections) {
          const quantity = Number.isFinite(selection.quantity) && selection.quantity > 0 ? selection.quantity : 0;
          total += quantity;
          selectedQuantities.set(selection.id, (selectedQuantities.get(selection.id) || 0) + quantity);
        }

        const rows = selections.map((selection) => {
          const variant = this.stockInventory.get(selection.id);
          const quantity = Number.isFinite(selection.quantity) && selection.quantity > 0 ? selection.quantity : 0;
          const increment = Math.max(1, Math.floor(Number(variant?.increment) || 1));
          const minimum = Math.ceil(Math.max(1, Number(variant?.min) || 1) / increment) * increment;
          const usedQuantity = (this.stockCart.get(selection.id) || 0) + ((selectedQuantities.get(selection.id) || 0) - quantity);
          const variantMaximum = variant?.max == null ? Infinity : Math.max(0, Number(variant.max) - usedQuantity);
          let maximum = Math.min(this.kitLimit, this.kitRowLimit, variantMaximum, this.kitLimit - (total - quantity));
          if (variant?.tracked === true) {
            const stock = Math.max(0, Number(variant.quantity) || 0);
            maximum = Math.min(maximum, stock - usedQuantity);
          }
          maximum = Math.max(0, Math.floor(maximum / increment) * increment);

          let error = '';
          if (!variant) {
            error = this.stockText('data-cart-check-error', 0);
          } else if (!Number.isInteger(selection.quantity) || selection.quantity < minimum) {
            error = theme.quickOrderListStrings.minError.replace('[min]', String(minimum));
          } else if (selection.quantity > maximum) {
            error = this.stockText('data-stock-limit', maximum);
          } else if (selection.quantity % increment !== 0) {
            error = theme.quickOrderListStrings.stepError.replace('[step]', String(increment));
          }

          const valid = Boolean(variant) && Number.isInteger(selection.quantity) && selection.quantity >= minimum && selection.quantity <= maximum && selection.quantity % increment === 0;
          return { ...selection, minimum, maximum, increment, error, valid, tracked: variant?.tracked === true };
        });

        let error = total > this.kitLimit ? this.stockText('data-order-limit', this.kitLimit) : rows.find((row) => row.error)?.error || '';
        if (this.stockCartFailure || !this.stockReady) error = this.stockText('data-cart-check-error', 0);
        return { rows, total, error, valid: this.stockReady && rows.length >= this.bundleMin && total <= this.kitLimit && rows.every((row) => row.valid) };
      }

      prepareStockRow(row) {
        const input = row.querySelector('quantity-input input');
        const label = row.querySelector('quantity-input label');
        const status = row.querySelector('[data-kit-stock-status]');
        if (!this.stockRowIds.has(row)) {
          this.stockRowSequence += 1;
          this.stockRowIds.set(row, `${this.id || 'inspirely-kit-bundle'}-stock-${this.stockRowSequence}`);
        }
        const id = this.stockRowIds.get(row);
        input.id = `${id}-quantity`;
        if (label) label.htmlFor = input.id;
        if (status) {
          const previousId = status.id;
          status.id = `${id}-status`;
          const descriptions = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter((value) => value && value !== previousId);
          input.setAttribute('aria-describedby', [...new Set([...descriptions, status.id])].join(' '));
        }
        return { input, status };
      }

      syncStockState() {
        if (!this.stockInventory || !this.stockCart) return { valid: false };
        const state = this.calculateStockState(this.getStockSelections());
        const selected = new Map(state.rows.map((row) => [row.row, row]));

        for (const row of this.variants) {
          const { input, status } = this.prepareStockRow(row);
          input.setCustomValidity('');
          input.removeAttribute('aria-invalid');
          const selection = selected.get(row);
          if (selection) {
            input.min = String(selection.minimum);
            input.max = String(selection.maximum);
            input.step = String(selection.increment);
            input.setAttribute('data-min', String(selection.minimum));
            input.setAttribute('data-max', String(selection.maximum));
            if (selection.error) {
              input.setCustomValidity(selection.error);
              input.setAttribute('aria-invalid', 'true');
            }
            if (status) {
              const message = selection.error || (selection.tracked ? this.stockText('data-stock-available', selection.maximum) : '');
              status.textContent = message;
              status.hidden = !message;
              status.toggleAttribute('data-invalid', Boolean(selection.error));
            }
          } else {
            input.max = String(Math.min(this.kitLimit, this.kitRowLimit));
            input.setAttribute('data-max', input.max);
            if (status) {
              status.textContent = '';
              status.hidden = true;
              status.removeAttribute('data-invalid');
            }
          }
          row.querySelector('quantity-input').validateQtyRules();
        }

        const error = this.querySelector('[data-kit-stock-error]');
        if (error) {
          error.textContent = state.error;
          error.hidden = !state.error;
        }
        this.submitButton.disabled = !state.valid || this.stockPreflight || this.stockRefreshing || this.submitButton.getAttribute('aria-busy') === 'true' || this.submitButton.getAttribute('aria-disabled') === 'true';
        return state;
      }

      onStockInput(event) {
        if (!event.target.matches('quantity-input input')) return;
        super.onChangeHandler();
        this.syncStockState();
      }

      getStockSubtotal() {
        return this.getStockSelections().reduce((subtotal, selection) => {
          const price = Number(selection.row.querySelector('[data-product-bundle-variant-price]')?.getAttribute('data-price'));
          return Number.isInteger(selection.quantity) && selection.quantity > 0 && Number.isFinite(price) ? subtotal + price * selection.quantity : subtotal;
        }, 0);
      }

      updateTotal() {
        const element = this.querySelector('[data-product-bundle-total]');
        if (element) element.innerHTML = theme.Currency.formatMoney(this.getStockSubtotal(), theme.settings.moneyFormat);
      }

      updateTotalWithCurrency() {
        const element = this.querySelector('[data-product-bundle-total-with-currency]');
        if (element) element.innerHTML = theme.Currency.formatMoney(this.getStockSubtotal(), theme.settings.moneyWithCurrencyFormat);
      }

      onChangeHandler() {
        super.onChangeHandler();
        this.syncStockState();
      }

      onAddHandler(event) {
        super.onAddHandler(event);
        this.syncStockState();
      }

      onRemoveHandler(event) {
        super.onRemoveHandler(event);
        this.syncStockState();
      }

      resetVariants() {
        super.resetVariants();
        this.syncStockState();
      }

      onStockCartUpdate(data) {
        if (Array.isArray(data?.cart?.items)) {
          this.setStockCart(data.cart.items);
          this.syncStockState();
        } else {
          this.refreshStockCart().catch(() => {});
        }
      }

      refreshStockCart() {
        if (this.stockCartRequest) return this.stockCartRequest;
        this.stockRefreshing = true;
        this.stockRequestController = new AbortController();
        const connection = this.stockConnection;
        const version = this.stockCartVersion;
        this.syncStockState();
        const request = (async () => {
          const response = await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET'), cache: 'no-store', signal: this.stockRequestController.signal });
          if (!response.ok) throw new Error('Cart quantity request failed');
          const cart = await response.json();
          if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
          if (connection !== this.stockConnection || !this.isConnected) return cart;
          if (this.stockCartVersion === version) this.setStockCart(cart.items);
          this.stockCartFailure = false;
          return cart;
        })().catch((error) => {
          if (connection === this.stockConnection && error.name !== 'AbortError') this.stockCartFailure = true;
          throw error;
        }).finally(() => {
          if (this.stockCartRequest !== request) return;
          this.stockRefreshing = false;
          this.stockCartRequest = null;
          if (this.isConnected) this.syncStockState();
        });
        this.stockCartRequest = request;
        return request;
      }

      async onSubmitHandler(event) {
        event.preventDefault();
        if (this.stockPreflight || this.stockRefreshing || this.submitButton.hasAttribute('aria-disabled') || this.submitButton.getAttribute('aria-busy') === 'true') return;
        if (!this.syncStockState().valid) return;
        const submitter = event.submitter || event.currentTarget || this.submitButton;
        const connection = this.stockConnection;
        const nativeEvent = { preventDefault: () => event.preventDefault(), submitter, currentTarget: submitter };
        this.stockPreflight = true;
        this.syncStockState();
        try {
          await this.refreshStockCart();
          if (connection !== this.stockConnection || !this.isConnected || !this.syncStockState().valid) return;
          this.stockPreflight = false;
          this.syncStockState();
          super.onSubmitHandler(nativeEvent);
          this.syncStockState();
        } catch {
          if (connection === this.stockConnection) this.stockCartFailure = true;
        } finally {
          if (connection === this.stockConnection) {
            this.stockPreflight = false;
            if (this.isConnected) this.syncStockState();
          }
        }
      }
    }

    customElements.define('inspirely-kit-bundle', InspirelyKitBundle);
  });
})();
